import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  adminLogin,
  getMfaResolver,
  completeMfaTotpSignIn,
  getAdminAuthErrorMessage,
} from '../services/firebase';
import { useLang } from '../lang/LangContext';

export default function Login() {
  const { lang, setLang, t } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA challenge state — TOTP (authenticator app) only.
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success(t('Welcome back'));
    } catch (err) {
      const resolver = getMfaResolver(err);
      if (resolver) {
        setMfaResolver(resolver);
        toast(t('Enter the 6-digit code from your authenticator app'), { icon: '🔐' });
      } else {
        toast.error(getAdminAuthErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (mfaCode.trim().length < 6) return;
    setLoading(true);
    try {
      const totpHint = mfaResolver.hints.find(
        (h) => h.factorId === 'totp' || h.factorId?.includes('totp')
      ) || mfaResolver.hints[0];
      await completeMfaTotpSignIn(mfaResolver, totpHint.uid, mfaCode);
      toast.success(t('2FA verified — welcome to Command Center'));
    } catch (err) {
      toast.error(getAdminAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const resetMfa = () => {
    setMfaResolver(null);
    setMfaCode('');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#070c18',
        backgroundImage: 'radial-gradient(at 0% 0%, rgba(0, 168, 107, 0.12) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.08) 0px, transparent 50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        {/* Brand Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img
            src="/icons/logo-wordmark.png"
            alt="MyTownly"
            style={{
              height: 52,
              width: 'auto',
              maxWidth: '85%',
              objectFit: 'contain',
              marginBottom: 8,
              filter: 'drop-shadow(0 4px 16px rgba(0, 168, 107, 0.25))',
            }}
          />
          <div
            style={{
              fontSize: 11,
              color: '#94a3b8',
              fontFamily: 'JetBrains Mono',
              letterSpacing: 2,
              fontWeight: 700,
            }}
          >
            {t('OPERATIONS COMMAND CENTER')}
          </div>
        </div>

        {/* Card Container */}
        <div
          className="card"
          style={{
            background: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 20,
            padding: 32,
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
          }}
        >
          {/* Language Switcher on Login */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => setLang('en')}
              style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                background: lang === 'en' ? 'var(--green)' : 'transparent',
                color: lang === 'en' ? '#04140c' : 'var(--text2)',
                borderColor: lang === 'en' ? 'var(--green)' : 'var(--border2)',
              }}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang('te')}
              style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                background: lang === 'te' ? 'var(--green)' : 'transparent',
                color: lang === 'te' ? '#04140c' : 'var(--text2)',
                borderColor: lang === 'te' ? 'var(--green)' : 'var(--border2)',
              }}
            >
              తె
            </button>
          </div>

          {!mfaResolver ? (
            /* Step 1: Email & Password */
            <>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  marginBottom: 6,
                  color: '#ffffff',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                }}
              >
                {t('Admin Sign In')}
              </div>
              <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 24 }}>
                {t('Enter credentials to access the fleet control desk.')}
              </p>

              <form onSubmit={handleLogin}>
                <div className="field-group">
                  <label className="field-label">{t('ADMIN EMAIL')}</label>
                  <input
                    type="email"
                    placeholder="admin@mytownly.in"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="field-group" style={{ marginTop: 16 }}>
                  <label className="field-label">{t('PASSWORD')}</label>
                  <input
                    type="password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary-large"
                  style={{ marginTop: 24 }}
                  disabled={loading}
                >
                  {loading ? t('Authenticating…') : t('Sign In to Portal →')}
                </button>
              </form>
            </>
          ) : (
            /* Step 2: Authenticator-app code */
            <>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  marginBottom: 6,
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                }}
              >
                <span>🔐</span>
                <span>{t('Two-Factor Authentication')}</span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>
                {t('Enter the current 6-digit code from your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, 1Password).')}
              </p>

              <form onSubmit={handleMfaSubmit}>
                <div className="field-group">
                  <label className="field-label">{t('6-DIGIT VERIFICATION CODE')}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="6"
                    placeholder="123456"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    style={{
                      fontSize: 22,
                      letterSpacing: 6,
                      textAlign: 'center',
                      fontWeight: 700,
                      fontFamily: 'JetBrains Mono, monospace',
                    }}
                    required
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary-large"
                  style={{ marginTop: 24 }}
                  disabled={loading || mfaCode.length < 6}
                >
                  {loading ? t('Verifying 2FA…') : t('Verify & Continue →')}
                </button>

                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ width: '100%', marginTop: 12, padding: 11 }}
                  onClick={resetMfa}
                >
                  {t('← Back to login')}
                </button>
              </form>
            </>
          )}

          <div
            style={{
              marginTop: 24,
              padding: '12px 14px',
              background: 'rgba(0, 168, 107, 0.08)',
              borderRadius: 10,
              border: '1px solid rgba(0, 168, 107, 0.25)',
              fontSize: 11.5,
              color: '#34d399',
              fontFamily: 'JetBrains Mono',
              lineHeight: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>🛡️</span>
            <span>{t('Secured with Firebase Multi-Factor Authentication (MFA).')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
